import classNames from "classnames";
import { useState } from "react";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { SeshAddonInput } from "../base_components/sesh_addon_input";
import { SimpleTable } from "../simple_table";
import { Line } from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { DateTime } from "luxon";
import { SeshSelect } from "../base_components/sesh_select_input";
import { ISeshStats } from "../../../../../../src/api/web_stats_cache";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);

type DataHeader = keyof ISeshStats;

const SortedHeadersRecord: Record<DataHeader, true> = {
	date: true,
	server_count: true,
	shard_count: true,
	total_user_count: true,
	total_event_count: true,
	future_event_count: true,
	poll_count: true,
	stripe_entitlement_count: true,
	discord_entitlement_count: true,
	patreon_count: true,
};

const SortedHeaders = Object.keys(SortedHeadersRecord);

type ViewType = "Table" | "Graph";

export function AdminSeshStats() {
	const stats_query = trpc.admin.stats.useQuery();

	const [view, setView] = useState<ViewType>("Graph");

	if (!stats_query.isSuccess) {
		return <LoadingThrobber />;
	}

	if (!stats_query.data[0]) {
		return <div>No data</div>;
	}

	return (
		<div className="flex flex-col gap-2">
			<SeshAddonInput
				value={view}
				setValue={setView}
				buttons={[
					{
						label: "Graph",
						value: "Graph",
					},
					{
						label: "Table",
						value: "Table",
					},
				]}
			/>
			{view === "Table" ? (
				<TableView data={stats_query.data} />
			) : (
				<GraphView data={stats_query.data} />
			)}
		</div>
	);
}

function TableView(props: {
	data: RouterInferenceHelpers["admin"]["stats"]["output"];
}) {
	const headers = SortedHeaders;

	return (
		<SimpleTable
			headers={headers}
			rows={props.data.map((val) =>
				headers.map((header) => val.data[header])
			)}
		/>
	);
}

function GraphView(props: {
	data: RouterInferenceHelpers["admin"]["stats"]["output"];
}) {
	const headers = SortedHeaders.filter((val) => val !== "date");
	const [tab, setTab] = useState(headers[0]);

	return (
		<div className="flex flex-col gap-2">
			<SeshSelect
				value={{
					label: tab,
					value: tab,
				}}
				options={headers.map((val) => ({
					label: val,
					value: val,
				}))}
				onChange={(new_value) => {
					setTab(new_value.value as string);
				}}
			/>
			<Line
				options={{
					scales: {
						y: {
							ticks: {
								precision: 0,
							},
							beginAtZero: true,
						},
					},
				}}
				data={{
					labels: props.data.map((val) =>
						DateTime.fromISO(
							val.created as unknown as string
						).toLocaleString({
							month: "short",
							day: "2-digit",
							year: "numeric",
						})
					),

					datasets: [
						{
							label: tab,
							data: props.data.map((val) => val.data[tab]),
							borderColor: "#00f4ff",
							backgroundColor: "#00f4ff",
							pointBackgroundColor: "rgba(255, 0, 0, 0.0)",
							pointBorderColor: "rgba(255, 0, 0, 0.0)",
							tension: 0.1,
							pointHitRadius: 50,
						},
					],
				}}
			/>
		</div>
	);
}

function Tabs(props: {
	tab: string;
	setTab: (new_tab: string) => void;
	tabs: Array<string>;
}) {
	const tabs: JSX.Element[] = [];

	for (const key of props.tabs) {
		tabs.push(
			<li
				key={key}
				className={classNames({
					"is-active bg-base-dark": props.tab === key,
				})}
				onClick={() => props.setTab(key)}
			>
				<a className="manual-link !bg-inherit">{key}</a>
			</li>
		);
	}

	return (
		<div className="tabs is-boxed">
			<ul>{tabs}</ul>
		</div>
	);
}
